import React from 'react';
import Phaser from 'phaser';
import { Box } from '@mui/material';
import { createTimeKeeper } from 'Models/createTimeKeeper';
import { TimingOffsetsConfig } from 'Models/EventStream';
import TimeSignature from 'Models/TimeSignature';
import { OSMDEventStream } from 'Phaser/GameObjects/OSMDEventStream';
import Scheduler2 from 'Utils/Scheduler2';
import { PhaserGame, type IRefPhaserGame } from 'Phaser/PhaserGame';
import { PhaserToReactListeners, NotifyPhaserEvents, RemoveListener } from 'Phaser/EventBus2';
import { useMidiContext, type NoteMessage } from 'Contexts/MidiContext';
import { FeatureFlag, useFeatureFlag } from 'Utils/FeatureFlags';
import type {
  ExerciseSessionControls,
  OSMDExerciseSessionInput,
} from 'Phaser/Scenes/OSMDExerciseScene';
import { resolveExerciseSceneKey } from 'Phaser/Scenes/exerciseSceneBinding';
import type { InteractionFamily } from 'Types/ExerciseContentTypes';
import type { ExerciseSessionEmitted } from 'Phaser/ExerciseSessionMachine/exerciseSessionMachine.types';
import { useDispatch, useSelector } from 'react-redux';
import type { MainAppReducer } from 'Types';
import logger from 'Utils/Logger';
import {
  playthroughCompleted,
  pointEarned,
  sessionCompleted,
  sessionStarted,
  hydrateRequested,
} from 'Reducers/exerciseProgressReducer';
import { useExerciseSessionReturnNavigation } from 'Hooks/useExerciseSessionReturnNavigation';

/**
 * The shared core of a played exercise sitting (MUS-481). Both the real,
 * user-facing play screen and the dev launcher mount the identical machinery -
 * the Phaser scene, the session-machine Redux bridge, the host MIDI bridge
 * (MUS-319), the playback scheduler injection (MUS-553), and the
 * return-to-roadmap navigation (MUS-321). This component is
 * the single home for that machinery so there is exactly one copy of each: a
 * second, drifting copy of the MIDI bridge or the return-nav is the regression
 * this extraction exists to prevent.
 *
 * It owns none of the control chrome. The caller renders that via `children`, a
 * render-prop handed the live `ExerciseSessionControls` (null until the scene is
 * ready). The real screen draws real Start / Submit / End; the dev launcher draws
 * its debug panel. The shell carries no write-state / frontier debug readouts.
 *
 * Gated on USE_EXERCISES: the scene is only registered when the flag is on, and
 * the MIDI bridge and return-nav are dark until then, so a shell mounted with the
 * flag off renders nothing playable.
 */

const GAME_WIDTH = 960;
const GAME_HEIGHT = 400;

/**
 * The sitting to launch: figure + exercise identity + content coordinate + config,
 * plus the interaction family that binds the sitting to a playable scene (MUS-456).
 * The first four fields are the scene's own input; `interaction_family` is content
 * metadata the shell resolves the scene from, so it is not part of the scene input.
 */
export type ExercisePlaySessionInput = Pick<
  OSMDExerciseSessionInput,
  'prompt' | 'exercise' | 'cell' | 'config'
> & { interaction_family: InteractionFamily };

interface ExercisePlayShellProps {
  session: ExercisePlaySessionInput;
  /**
   * Optional observation seam over the machine's emitted events, in addition to
   * the internal Redux bridge that always runs. The dev launcher uses it to keep
   * an at-a-glance event log; the real screen passes nothing.
   */
  onSessionEvent?: (event: ExerciseSessionEmitted) => void;
  /** Render-prop for control chrome, handed the live controls (null until ready). */
  children?: (controls: ExerciseSessionControls | null) => React.ReactNode;
  /**
   * Spread onto the outer container - lets a caller attach its own data hooks.
   * The `data-*` index signature is what makes a `{ 'data-...': ... }` literal
   * type-check here: MUI's Box props accept data attributes as bare JSX props but
   * not through a plain props object without it.
   */
  containerProps?: React.ComponentProps<typeof Box> & {
    [dataAttr: `data-${string}`]: string | number | boolean | undefined;
  };
}

const ExercisePlayShell: React.FC<ExercisePlayShellProps> = ({
  session,
  onSessionEvent,
  children,
  containerProps,
}) => {
  const enabled = useFeatureFlag(FeatureFlag.USE_EXERCISES);
  const dispatch = useDispatch();
  // MUS-319: the exercise scene inherits the base MidiOn/MidiOff listeners, but the
  // host has to produce those events. Bridge the app's MIDI stream (Web MIDI on
  // web, the native/Bluetooth bridge on device) into Phaser, mirroring PianoDebug.
  const { addListener, removeListener } = useMidiContext();
  // Return-to-roadmap seam (MUS-321): on sessionCompleted the slice parks the
  // summary; this hook navigates to /exercises off that edge. Flag-guarded.
  useExerciseSessionReturnNavigation();

  const phaserRef = React.useRef<IRefPhaserGame | null>(null);
  const [controls, setControls] = React.useState<ExerciseSessionControls | null>(null);
  const [timeKeeper] = React.useState(() => createTimeKeeper());
  const [eventStream] = React.useState(
    () => new OSMDEventStream(timeKeeper, new TimingOffsetsConfig(), null, 1, 0.025, 0.1, true),
  );

  // MUS-553: the base scene's playback machine (count-in, cursor, note judging) is
  // only instantiated when the host injects a Scheduler2 - the same seam the
  // repertoire container uses. Without it every transport event is dropped and the
  // TimeKeeper never unpauses. The scheduler must share the event stream's
  // TimeKeeper. Created lazily on first injection and destroyed on unmount, so a
  // StrictMode mount/unmount/mount never leaves the live scene holding a destroyed
  // scheduler.
  //
  // The user's metronome + downbeat sound preferences apply to the scheduler's
  // post-count-in clicks (the scene's own count-in click reads the same flag),
  // mirroring the repertoire play context. Default on when unset.
  const metronomeSound = useSelector(
    (state: MainAppReducer) => state.mainAppReducer?.userData?.rep_metronome_sound ?? true,
  );
  const downbeatsSound = useSelector(
    (state: MainAppReducer) => state.mainAppReducer?.userData?.rep_downbeats_sound ?? true,
  );
  const soundPrefsRef = React.useRef({ metronomeSound, downbeatsSound });
  soundPrefsRef.current = { metronomeSound, downbeatsSound };

  const schedulerRef = React.useRef<Scheduler2 | null>(null);
  const getScheduler = React.useCallback(() => {
    if (schedulerRef.current === null) {
      const scheduler = new Scheduler2(timeKeeper);
      void scheduler.changeMetronomeAudio(soundPrefsRef.current.metronomeSound);
      void scheduler.changeDownbeatsAudio(soundPrefsRef.current.downbeatsSound);
      schedulerRef.current = scheduler;
    }
    return schedulerRef.current;
  }, [timeKeeper]);
  React.useEffect(() => {
    void schedulerRef.current?.changeMetronomeAudio(metronomeSound);
  }, [metronomeSound]);
  React.useEffect(() => {
    void schedulerRef.current?.changeDownbeatsAudio(downbeatsSound);
  }, [downbeatsSound]);
  React.useEffect(
    () => () => {
      schedulerRef.current?.destroy();
      schedulerRef.current = null;
    },
    [],
  );

  // Latest session + observation seam, read through refs so the scene-launch
  // callback stays stable and does not re-fire on prop identity changes.
  const sessionRef = React.useRef(session);
  sessionRef.current = session;
  const onSessionEventRef = React.useRef(onSessionEvent);
  onSessionEventRef.current = onSessionEvent;

  const startScene = React.useCallback(() => {
    if (!phaserRef.current?.game) return;

    const { prompt, exercise, cell, config, interaction_family } = sessionRef.current;

    // MUS-456: resolve the playable scene from the exercise's interaction family via
    // the scene-config binding, rather than starting a hardcoded scene key. A family
    // with no playable scene yet (manipulated / answered) resolves to null; guard
    // before any session side effect so nothing is dispatched for a scene that never
    // opens.
    const sceneKey = resolveExerciseSceneKey(interaction_family);
    if (sceneKey === null) {
      logger.warn(
        `[ExercisePlayShell] no playable scene bound for interaction family "${interaction_family}"; not launching`,
      );
      return;
    }

    // MUS-553: the TimeKeeper must know the meter before the transport can run -
    // TimeKeeper.unpause() silently returns (stays paused) when it has no time
    // signature, so the scheduler would start the machine but the clock, cursor and
    // note judging would never move. Host-owned, as in the repertoire context; the
    // scene renders the same meter from the same prompt field. Idempotent on restart.
    const [numerator, denominator] = prompt.time_signature;
    // The prompt's beat grouping (if any) rides on the meter, so the clock,
    // metronome and count-in all play it.
    timeKeeper.setTimeSignatures(0, [
      new TimeSignature(numerator, denominator, prompt.beat_grouping),
    ]);

    // A sitting is opening: tell the slice which exercise it belongs to (the saga
    // attributes earned clears to it) and hydrate its existing frontier. Both are
    // dark behind USE_EXERCISES via the saga guard.
    dispatch(sessionStarted({ exerciseId: exercise.id }));
    dispatch(hydrateRequested({ exerciseId: exercise.id }));

    const exerciseSession: OSMDExerciseSessionInput = {
      prompt,
      exercise,
      cell,
      config,
      // The engine's emitted facts become Redux actions: the saga relays pointEarned
      // to the backend and drives navigation off sessionCompleted; the slice carries
      // feedback + the frontier mirror. onSessionEvent is the optional caller seam.
      onSessionEvent: (event: ExerciseSessionEmitted) => {
        onSessionEventRef.current?.(event);
        switch (event.type) {
          case 'playthroughCompleted':
            dispatch(
              playthroughCompleted({
                cell: event.cell,
                config: event.config,
                accuracy: event.accuracy,
                verdict: event.verdict,
                playthroughIndex: event.playthroughIndex,
              }),
            );
            break;
          case 'pointEarned':
            dispatch(
              pointEarned({ cell: event.cell, config: event.config, mastered: event.mastered }),
            );
            break;
          case 'sessionCompleted':
            dispatch(sessionCompleted({ summary: event.summary }));
            break;
          default: {
            // Exhaustiveness guard: a new emitted variant fails to compile here until
            // it is handled, instead of being silently dropped from the Redux bridge.
            const _exhaustive: never = event;
            return _exhaustive;
          }
        }
      },
      onReady: (sessionControls: ExerciseSessionControls) => {
        // MUS-553: inject the scheduler here, not right after scene.start. The
        // Phaser game is a reused singleton, so at scene.start time a previous
        // instance of this scene can still be alive with its listener registered;
        // an emit then would reach that dying instance and the restarted one would
        // boot with no scheduler. onReady runs in create(), after this instance's
        // init() registered its listener, so delivery is direct and to the live
        // scene. It precedes handing out the controls, so no user Start can reach
        // the transport before the machine is running.
        const scheduler = getScheduler();
        NotifyPhaserEvents.InjectScheduler2({ scheduler2: scheduler });
        setControls({
          ...sessionControls,
          start: () => {
            // MUS-553: initialize the scheduler's audio inside the Start gesture,
            // as the repertoire host does on its first Start. init() unlocks the
            // AudioContext (Tone.start must run in a user gesture) and decodes the
            // metronome click samples; without it the scheduler's post-count-in
            // metronome schedules silent no-ops. The samples are only needed after
            // the first played note, so the async decode is not awaited. Idempotent.
            scheduler
              .init({ tempo: sessionRef.current.config.tempo })
              .catch((error: unknown) =>
                logger.warn('[ExercisePlayShell] scheduler audio init failed:', error),
              );
            sessionControls.start();
          },
        });
      },
    };

    phaserRef.current.game.scene.start(sceneKey, {
      eventStream,
      width: GAME_WIDTH,
      height: GAME_HEIGHT,
      performanceTempo: config.tempo,
      cursorVisible: true,
      scoreTheme: 'light',
      exerciseSession,
    });
  }, [eventStream, timeKeeper, dispatch, getScheduler]);

  React.useEffect(() => {
    if (!enabled) return undefined;
    PhaserToReactListeners.PhaserReady(startScene, undefined, false, true);
    if (phaserRef.current?.wasReused) startScene();
    return () => {
      RemoveListener('PhaserReady', startScene);
    };
  }, [enabled, startScene]);

  // MUS-319: forward real MIDI note events into the Phaser scene. The scene's
  // inherited base handler ignores them until a take's count-in arms playback, then
  // scores each played note against the event stream that drives the graded bands.
  React.useEffect(() => {
    if (!enabled) return undefined;
    const listener = {
      name: 'exercise-play-midi-bridge',
      noteOn: (ev: NoteMessage) => {
        if (ev.velocity === 0) {
          NotifyPhaserEvents.MidiOff({ note: ev.note });
          return;
        }
        NotifyPhaserEvents.MidiOn({ note: ev.note, velocity: ev.velocity });
      },
      noteOff: (ev: NoteMessage) => NotifyPhaserEvents.MidiOff({ note: ev.note }),
    };
    addListener(listener);
    return () => removeListener(listener);
  }, [enabled, addListener, removeListener]);

  if (!enabled) return null;

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100vw',
        height: '100vh',
        overflow: 'hidden',
        backgroundColor: '#F4E5C7',
      }}
      {...containerProps}
    >
      <style>{`#game-container { pointer-events: auto !important; }`}</style>
      <PhaserGame
        ref={phaserRef}
        width={GAME_WIDTH}
        height={GAME_HEIGHT}
        scaling={Phaser.Scale.NONE}
        crisp
      />
      {children?.(controls)}
    </Box>
  );
};

export default ExercisePlayShell;
